import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 mb-8">
          <ArrowLeft size={14} /> Back to AdBit
        </a>

        <h1 className="text-3xl font-bold text-slate-800 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Privacy Policy</h1>
        <p className="text-lg text-slate-500 mb-2">Aviso de Privacidad Integral</p>
        <p className="text-sm text-slate-400 mb-10">Last updated: April 2026</p>

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-8">
          <p className="text-xs text-orange-700 leading-relaxed">
            In compliance with the Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP 2025) and the EU General Data Protection Regulation (GDPR) for users in the European Economic Area.
          </p>
        </div>

        <div className="prose prose-slate prose-sm max-w-none">
          <Section title="1. Data Controller">
            <p>The data controller responsible for your personal data is <strong>Bruno Serna Bercht</strong>, individual entrepreneur (persona física con actividad empresarial), operating under the commercial name "AdBit", with address for notifications at: La Iliada 21, Lomas de Axomiatla, C.P. 01820, Álvaro Obregón, Ciudad de México, Mexico.</p>
            <p>For data protection matters: <strong>support@adbit.digital</strong></p>
            <p>For general legal matters: <strong>support@adbit.digital</strong></p>
          </Section>

          <Section title="2. Personal Data Collected">
            <p>We collect the following categories of personal data. None constitutes sensitive personal data as defined by the LFPDPPP:</p>
            <p><strong>Identification Data:</strong> Full name, email address.</p>
            <p><strong>Authentication Data:</strong> Password (stored exclusively as a cryptographic hash — never in plain text).</p>
            <p><strong>Usage Data:</strong> Analysis history, uploaded images, analysis results, folder organization, team membership.</p>
            <p><strong>Technical Data:</strong> IP address, browser type, device information, access timestamps.</p>
            <p><strong>Billing Data:</strong> Subscription plan, billing history (payment credentials processed by Stripe — we do not store card numbers).</p>
            <p><strong>Tax Data (Mexican customers):</strong> RFC, fiscal regime when requesting CFDI invoices through Gigstack.</p>
            <p><strong>Sensitive Data:</strong> We do not intentionally collect sensitive personal data. If uploaded content incidentally contains such data, it is processed solely for analysis and is not extracted or stored separately.</p>
          </Section>

          <Section title="3. Legal Basis for Processing">
            <p><strong>Mexico (LFPDPPP 2025):</strong> Tacit consent for necessary purposes; express consent for optional purposes.</p>
            <p><strong>European Economic Area (GDPR):</strong> Contract performance for service delivery; legitimate interest for security and platform improvement; consent for marketing communications.</p>
          </Section>

          <Section title="4. Purposes of Data Processing">
            <h4 className="font-semibold text-slate-700 mt-4">4.1 Necessary Purposes (do not require additional consent):</h4>
            <p>Account management, authentication, image analysis processing, generating results, payment processing, tax invoicing (CFDI), transactional communications, enforcing plan limits, customer support, and legal compliance.</p>

            <h4 className="font-semibold text-slate-700 mt-4">4.2 Optional Purposes (require your consent):</h4>
            <p>Promotional communications, anonymized benchmarks for scoring calibration, feedback surveys, website analytics (Google Analytics), user experience analysis (Microsoft Clarity session recordings and heatmaps), and advertising measurement (Meta Pixel/Conversion API).</p>
            <p>You may revoke consent for any optional purpose at any time by contacting support@adbit.digital.</p>
          </Section>

          <Section title="5. Data Sharing and Third-Party Processors">
            <p>Your data may be shared with:</p>
            <p><strong>Google Cloud Vision API</strong> — visual element detection</p>
            <p><strong>Anthropic Claude</strong> — AI analysis and recommendations</p>
            <p><strong>Stripe</strong> — payment processing (PCI-DSS Level 1)</p>
            <p><strong>Gigstack</strong> — CFDI invoice generation</p>
            <p><strong>MongoDB Atlas</strong> — data storage (AES-256 encryption)</p>
            <p><strong>Vercel / Railway</strong> — application hosting</p>
            <p><strong>Google Analytics</strong> — website usage analytics (Measurement ID: G-C1MJ333LKE)</p>
            <p><strong>Meta Pixel / Conversion API</strong> — advertising measurement and optimization (Pixel ID: 1316242720526518)</p>
            <p><strong>Microsoft Clarity</strong> — session recordings, heatmaps, and user behavior analytics (Project ID: wfvs98lwr1)</p>
            <p>We do not sell, rent, or trade your personal data to third parties for their own marketing purposes.</p>
          </Section>

          <Section title="6. International Data Transfers">
            <p>Your data may be transferred to and processed in the United States. By using the Platform, you consent to this transfer. We rely on the data protection commitments of our service providers.</p>
          </Section>

          <Section title="7. Data Retention">
            <p><strong>Account Data:</strong> Active account + 30 days after deletion.</p>
            <p><strong>Analysis Results / Images:</strong> Deleted within 30 days of account deletion.</p>
            <p><strong>Billing Records:</strong> Retained per fiscal law (5 years).</p>
            <p><strong>Technical Logs:</strong> Maximum 90 days.</p>
          </Section>

          <Section title="8. Security Measures">
            <p>We implement HTTPS/TLS encryption, bcrypt password hashing, AES-256 database encryption, role-based access control, rate limiting, and HTTP-only session cookies. No method of transmission is 100% secure.</p>
          </Section>

          <Section title="9. User Rights">
            <h4 className="font-semibold text-slate-700 mt-4">Mexico (ARCO Rights):</h4>
            <p><strong>Access, Rectification, Cancellation, Opposition.</strong> Send requests to support@adbit.digital with your name, account email, and the right you wish to exercise. We respond within 20 business days.</p>

            <h4 className="font-semibold text-slate-700 mt-4">EEA (GDPR):</h4>
            <p>You additionally have the right to data portability, restriction of processing, and to lodge a complaint with your local data protection authority.</p>
          </Section>

          <Section title="10. Automated Processing">
            <p>The Platform uses AI for analysis. You may object to automated processing where legally applicable by contacting support@adbit.digital.</p>
          </Section>

          <Section title="11. Cookies and Tracking">
            <p><strong>Essential:</strong> Authentication cookies (JWT) — required for the Platform to function.</p>
            <p><strong>Analytics:</strong> Google Analytics (G-C1MJ333LKE) — collects anonymized usage data including pages visited, session duration, traffic sources, and device information. Microsoft Clarity (wfvs98lwr1) — records user sessions, generates heatmaps, and tracks clicks and scroll behavior to help us improve the user experience. Clarity may collect IP address, device and browser information, and interaction data. Both are activated only with your consent.</p>
            <p><strong>Advertising:</strong> Meta Pixel (1316242720526518) — tracks browser-side events such as page views, sign-ups, and purchases. Meta Conversions API — sends the same events server-side for improved accuracy. Both are used to measure ad performance, optimize campaigns, and build retargeting audiences. Data sent to Meta may include hashed email addresses, IP address, browser user agent, and Facebook click/browser identifiers (fbc/fbp cookies). Activated only with your consent.</p>
            <p>Non-essential cookies are loaded only after you provide consent through our cookie banner. You can manage your preferences at any time via <a href="/cookie-settings" className="text-orange-600 hover:underline">Cookie Settings</a>.</p>
          </Section>

          <Section title="12. Changes to this Policy">
            <p>Material changes will be communicated at least 15 days in advance via email or in-platform notification.</p>
          </Section>

          <Section title="13. Supervisory Authority">
            <p><strong>Mexico:</strong> Secretaría Anticorrupción y Buen Gobierno.</p>
            <p><strong>EEA:</strong> Your local data protection authority.</p>
          </Section>

          <Section title="14. Contact">
            <p>ARCO requests and data protection: <strong>support@adbit.digital</strong></p>
            <p>General legal matters: <strong>support@adbit.digital</strong></p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-base font-bold text-slate-800 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
      <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
